import * as React from "react";
import { Box, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { BG, CARD_BG, AMBER } from "../../../Global/colors";
import { FONT_FAMILY, OK_GREEN, FA_BOTTOM_NAV_HEIGHT } from "../designTokens";
import { FASectionLabel, FANotesField } from "../FALayout";
import { FAGhostButton, FAPrimaryButton } from "../FAControls";
import { useGetFieldAdminHSDayQuery } from "../fieldAdminApi";

const TOTAL_STEPS = 5;

// A signed-off day refuses every write with a 409. The step pages had no idea: they let you
// edit and tap Save, the request came back 409 with a message saying to reopen the day, and
// saveAll turned that into a bare `false` that nothing rendered — so the page simply did
// nothing and gave no reason. Read the lock once, here, rather than in each of the five steps;
// RTK Query dedupes it against the request the hub already makes.
function useDayLocked() {
  const { data } = useGetFieldAdminHSDayQuery();
  return Boolean(data?.locked);
}

const CONTAINER_PX = { xs: "16px", sm: "32px" };

// The step strip is a full-bleed band in the design (a sibling of the content container,
// not a child of it), so HSPageShell renders it rather than the pages passing it as a
// child — a centred max-width container can't be escaped from the inside.
function DayLockedBanner() {
  const navigate = useNavigate();
  const locked = useDayLocked();
  if (!locked) return null;
  return (
    <Box sx={{ px: CONTAINER_PX, pt: "16px", maxWidth: "860px", mx: "auto" }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
        px: "14px", py: "11px", borderRadius: "10px",
        backgroundColor: "rgba(253,185,64,0.06)", border: "1px solid rgba(253,185,64,0.28)",
      }}>
        <LockOutlinedIcon sx={{ fontSize: "1rem", color: AMBER, flexShrink: 0 }} />
        <Typography sx={{ flex: 1, minWidth: "200px", fontSize: "0.82rem", color: "rgba(255,255,255,0.75)" }}>
          This day has been signed off and is locked. Changes here will not save until it is reopened.
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => navigate("/field-admin/hs/")}
          sx={{
            fontFamily: FONT_FAMILY, fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
            px: "10px", py: "6px", borderRadius: "7px", whiteSpace: "nowrap",
            background: "transparent", border: "1px solid rgba(253,185,64,0.3)", color: AMBER,
            "&:hover": { backgroundColor: "rgba(253,185,64,0.1)" },
          }}
        >
          Reopen from the hub
        </Box>
      </Box>
    </Box>
  );
}

export function HSPageShell({ step, children }) {
  return (
    <>
      <Helmet>
        <style>{`body { background: ${BG}; font-family: ${FONT_FAMILY}; }`}</style>
      </Helmet>
      {/* 59px clears the fixed top bar. */}
      <Box sx={{ pt: "59px" }}>
        {step ? <HSStepStrip {...step} /> : null}
        {step ? <DayLockedBanner /> : null}
        <Box
          sx={{
            px: CONTAINER_PX,
            pt: "24px",
            // The five step pages all render the fixed footer above the bottom nav, so
            // they need to clear both. The shell's other users (Site detail, Export,
            // History) have no footer and only need to clear the nav.
            pb: step ? "150px" : "120px",
            maxWidth: "860px",
            mx: "auto",
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
}

export function HSStepStrip({ stepNumber, title, countLabel }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, borderBottom: "1px solid rgba(255,255,255,0.06)", pt: "14px", pb: "16px" }}>
      <Box sx={{ px: CONTAINER_PX, maxWidth: "860px", mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px" }}>
          <Box
            sx={{
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              backgroundColor: AMBER,
              color: BG,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "0.8rem",
              flexShrink: 0,
            }}
          >
            {stepNumber}
          </Box>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: "0.92rem", fontWeight: 600, color: "rgba(255,255,255,0.9)" }}>
            <Box component="span" sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 500, mr: "4px" }}>
              Step {stepNumber} of {TOTAL_STEPS} ·
            </Box>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(255,255,255,0.4)", letterSpacing: "0.03em", flexShrink: 0, whiteSpace: "nowrap" }}>
            {countLabel}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "4px" }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((seg) => (
            <Box
              key={seg}
              sx={{
                flex: 1,
                height: "6px",
                borderRadius: "3px",
                backgroundColor: seg < stepNumber ? OK_GREEN : seg === stepNumber ? AMBER : "rgba(255,255,255,0.1)",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// Kept as a name so the nine H&S call sites read in their own idiom, but it is now the
// shared FASectionLabel underneath rather than a fourth copy of the same markup. The sx
// preserves the H&S pages' own vertical rhythm, which differs from the default.
export function HSSectionLabel({ children, right }) {
  return (
    <FASectionLabel right={right} sx={{ pt: "6px", pb: "6px", mb: "10px" }}>
      {children}
    </FASectionLabel>
  );
}

export function HSFooterActions({ onSaveExit, onMarkComplete, markCompleteLabel = "Mark complete →", markCompleteDisabled, saveExitPath = "/field-admin/hs/" }) {
  const navigate = useNavigate();
  // Both actions write, so neither can succeed while the day is locked. Disabling them is
  // what makes the banner above honest — offering a button that always fails is worse than
  // not offering it.
  const dayLocked = useDayLocked();
  // Leaving the step is only safe once the save landed. onSaveExit returning false means
  // it failed, so we stay put and let the page surface the error rather than navigating
  // away from work that was never persisted. Handlers that return nothing still navigate.
  const handleSaveExit = async () => {
    // Locked: leaving is still fine, writing is not. Firing a save that is certain to 409
    // would put us back where this started — a request that fails with nothing to show for it.
    if (dayLocked) { navigate(saveExitPath); return; }
    const saved = onSaveExit ? await onSaveExit() : true;
    if (saved === false) return;
    navigate(saveExitPath);
  };
  return (
    // Fixed and blurred per the design, but stacked ABOVE the bottom nav rather than at
    // bottom:0 — the design's H&S pages have no bottom nav at all, so its bar sits on the
    // floor of the phone frame. Full-bleed with the inner row capped at the content width,
    // the same treatment the bottom nav uses.
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: FA_BOTTOM_NAV_HEIGHT,
        backgroundColor: "rgba(13,17,23,0.94)",
        backdropFilter: "blur(12px)",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        pt: "14px",
        pb: "16px",
        zIndex: 79,
      }}
    >
      <Box sx={{ display: "flex", gap: "10px", px: CONTAINER_PX, maxWidth: "860px", mx: "auto" }}>
        <FAGhostButton onClick={handleSaveExit} sx={{ flex: 1, py: "13px", borderRadius: "10px", fontSize: "0.9rem" }}>
          {dayLocked ? "Back to hub" : "Save & exit"}
        </FAGhostButton>
        <FAPrimaryButton onClick={onMarkComplete} disabled={markCompleteDisabled || dayLocked} sx={{ flex: 1, py: "13px", borderRadius: "10px", fontSize: "0.9rem" }}>
          {markCompleteLabel}
        </FAPrimaryButton>
      </Box>
    </Box>
  );
}

export function HSNotesBlock({ label = "Section notes", value, onChange, placeholder, sub }) {
  return (
    // A card, with the field recessed to the page ground inside it. This was inverted
    // before: the textarea used the card colour and there was no card, so the field read
    // as a raised block on the background rather than a well inside a panel.
    <Box sx={{ backgroundColor: CARD_BG, border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", px: "16px", py: "14px", mt: "24px" }}>
      <Typography sx={{ fontSize: "0.76rem", fontWeight: 600, color: "rgba(255,255,255,0.65)", mb: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
        {label}
        <Box component="span" sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Optional
        </Box>
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", mb: "10px" }}>{sub}</Typography>
      ) : null}
      <FANotesField value={value} onChange={onChange} placeholder={placeholder} />
    </Box>
  );
}
